/**
 * The frame: providers, the auth gate, the TOTP gate, the stream
 * lifecycle, and the router. Modules are composed here and nowhere
 * else.
 */
import { useEffect, useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { API_BASE, auth, connectStream, initTracing, queryClient } from '../api';
import {
  Dispatch,
  SignIn,
  TotpEnroll,
  apexUrl,
  isApexHost,
  needsTotpEnrollment,
  orgFromEmail,
  orgFromHost,
  signOutUser,
  watchUser,
  type User,
} from '../modules/auth';
import { ThemeProvider } from '../theme';
import { ModeToggle, Viewport } from './chrome';
import { buildRouter, setSessionUser } from './routeTree';

const router = buildRouter('main');

// The browser is a process (2026-09-19): its traces root here, per interaction, and leave through
// the api's door with the signed-in user's bearer. Wired once, before the first render, so the
// page load is the first interaction.
initTracing(
  { url: `${API_BASE}/v1/traces`, token: async () => (auth.currentUser ? auth.currentUser.getIdToken() : null) },
  router,
);

export function App() {
  // undefined = auth state not yet known (avoids a sign-in flash on load)
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [enrolledTick, setEnrolledTick] = useState(0); // re-render after TOTP enrollment

  useEffect(() => watchUser(setUser), []);

  // One stream per signed-in session: the api bridge invalidates
  // queries; modules subscribe for ephemeral events.
  useEffect(() => {
    if (!user) return;
    return connectStream(user);
  }, [user]);

  // EVERY SCREEN IS THEMED: the provider wraps the choice, and the
  // choice is what varies. The palette follows the machine's
  // light/dark preference unless somebody overrode it (theme/mode.ts),
  // and every stratum reads it from here (docs/STRATA.md).
  return (
    <ThemeProvider>
      <Viewport>
        <ModeToggle />
        <Screen user={user} enrolledTick={enrolledTick} onEnrolled={() => setEnrolledTick(1)} />
      </Viewport>
    </ThemeProvider>
  );
}

/** Which screen this browser is owed, in gate order. */
function Screen({
  user,
  enrolledTick,
  onEnrolled,
}: {
  user: User | null | undefined;
  enrolledTick: number;
  onEnrolled: () => void;
}) {
  // THE FRONT DOOR, ahead of the auth gate on purpose: the apex has no
  // org, so it has no Identity Platform tenant to sign anybody in
  // against. It asks which workspace and redirects there, where the
  // subdomain selects the tenant.
  if (isApexHost(window.location.hostname)) return <Dispatch />;

  if (user === undefined) return null; // auth state still resolving
  if (user === null) return <SignIn />;

  // WRONG RECORD. Somebody with a real, valid identity for ANOTHER org
  // arrived here -- by typing the address, following a stale link, or
  // switching accounts mid-session. They go back to the front door.
  //
  // This is TIDINESS, not enforcement. core/platform/auth.py refuses
  // the token on every request, before a role or a route or a query,
  // and would keep refusing it if this block vanished. What this adds
  // is not being left staring at an app that answers 403 to everything.
  //
  // Only a RECORD can be the wrong one. A developer's machine names no
  // org (host law), so the emulator's made-up addresses stay signed in
  // -- and there is no apex to bounce them to anyway.
  const org = orgFromHost(window.location.hostname);
  if (org !== null && orgFromEmail(user.email ?? '') !== org) {
    void signOutUser().finally(() => window.location.assign(apexUrl(window.location)));
    return null;
  }
  // Two-factor, always: a signed-in user with no enrolled factor
  // enrolls before touching anything (emulator dev exempt).
  if (needsTotpEnrollment(user) && enrolledTick === 0) {
    return <TotpEnroll user={user} onEnrolled={onEnrolled} />;
  }

  setSessionUser(user);
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
